import * as SignalR from "@microsoft/signalr";
import EventEmitter from "events";
import _Vue from 'vue';
import { LOCALSTORAGE_AUTH2 } from "../../common/constants";
import { SOCKET_METHOD } from "../../common/socket.constants";
import { Oauthorization2Response } from "../../utils/authorization/data-model";

const defaultOptions = {
  log: true
};

interface ISocketConnection {
  options: any;
  connection: any;
  listened: any;
  socket: any;
  toSend: any;
  offline: boolean;
  offEvent: (method: string, callback: any) => void;
  start: (options: any) => Promise<any>;
  onEvent: (method: string, callback: any) => void;
}

class SocketConnection extends EventEmitter implements ISocketConnection {
  options: any;
  connection: any;
  listened: Array<any>;
  socket: any;
  toSend: any;
  offline: boolean;
  pendingMethods: Array<string>
  constructor(connection) {
    super();
    this.connection = connection;
    this.listened = [];
    this.socket = false;
    this.toSend = [];
    this.offline = false;
    this.pendingMethods = [];
  }

  async _initialize(connection = "") {
    try {
      const con = connection || this.connection;
      const token = this.options.accessToken || "";
      if (this.socket) this.socket = false;
  
      const socket = new SignalR.HubConnectionBuilder()
        .withUrl(con, {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: SignalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryAttempt => {
            return Math.min(30000, Number(retryAttempt) * 5000); // Exponential backoff, max 30s
          }
        })
        .configureLogging(SignalR.LogLevel.Information)
        .build();
  
      // Set timeout settings
      // socket.keepAliveIntervalInMilliseconds = 180000; // Keep-alive ping every 3 minutes
      // socket.serverTimeoutInMilliseconds = 300000; // Server timeout of 5 minutes
  
      socket.onclose = async error => {
        if (this.options.log) console.log("Socket disconnected, attempting to reconnect...");
        
        this.socket = false;
  
        const { statusCode } = error as any;
        if (statusCode && statusCode === 401) {
          this.handleSessionExpired();
        }
  
        await this._initialize(); // Attempt reconnection
        this.emit("reconnect");
      };
  
      await socket.start();
      this.socket = socket;
      this.emit("init");
      this.listenOnPendingMethods();
    } catch (error) {
      if (this.options.log) console.log("Error reconnecting:", error);
  
      const { statusCode } = error as any;
      if (statusCode && statusCode === 401) {
        this.handleSessionExpired();
      }
  
      // Retry after a delay if initialization fails
      // setTimeout(() => {
      //   this._initialize();
      // }, 5000); // Retry every 5 seconds
    }
  }

  private listenOnPendingMethods() {
    this.pendingMethods.forEach(method => {
      this.listened.push(method);
      this.handleListenMethod(method);
    })
    this.pendingMethods = [];
  }

  private handleListenMethod(method) {
    console.log(`Handle listen on method: ${method}`);
    this.socket.on(method, (name, data) => {
      if (this.options.log) console.log({ type: "receive", method, name, data });
      if (method === SOCKET_METHOD.DATA_CHANGE && name?.length > 0) {
        const ignoreSkipThisRowItem = name.filter(item => !item?.SkipThisRow);
        if (ignoreSkipThisRowItem?.length === 0) return;
        name = ignoreSkipThisRowItem; // Filter the skipped row data
      }
      this.emit(method, { name, data });
    });
  }

  handleSessionExpired() {
    const auth2Json = localStorage.getItem(LOCALSTORAGE_AUTH2);
    if (auth2Json != null) {
      let auth2: Oauthorization2Response = JSON.parse(auth2Json);
      this.options.accessToken = auth2.data.oauth2.accessToken;
    }
  }

  async start(options = {}) {
    this.options = Object.assign(defaultOptions, options);
    await this._initialize();
  }

  onEvent(method, callback) {
    console.log('socket onEvent');
    this.listen(method);
    this.on(method, callback);
  }

  listen(method) {
    console.log('socket listen');
    if (this.offline) return;
    if (this.listened.some(v => v === method) || this.pendingMethods.some(v => v === method)) return;
    if (this.socket) {
      // if (this.listened.some(v => v === method)) return;
      this.listened.push(method);
      this.handleListenMethod(method);
    } else {
      // const isPending = this.pendingMethods.findIndex(item => item === method) > -1
      // if(!isPending) this.pendingMethods.push(method);
      this.pendingMethods.push(method);
    }
    

    // this.on("init", () => {
    //   console.log('on init');
    //   this.socket.on(method, (name, data) => {
    //     if (this.options.log) console.log({ type: "receive", method, name, data });

    //     this.emit(method, { name, data });
    //   });
    // });
  }

  offEvent(method, callback) {
    console.log(`Off listen method: ${method}`)
    this.removeListener(method, callback)
    if (this.socket) {
      if(this.listenerCount(method) <= 0) {
        this.socket.off(method);
        const index = this.listened.findIndex(item => item === method);
        if (index > -1) this.listened.splice(index, 1);
      }
    }
    
  }

  send(methodName, ...args) {
    if (this.options.log) console.log({ type: "send", methodName, args });
    if (this.offline) return;

    if (this.socket) {
      this.socket.send(methodName, ...args);
      return;
    }

    this.once("init", () => this.socket.send(methodName, ...args));
  }

  async invoke(methodName, ...args) {
    if (this.options.log) console.log({ type: "invoke", methodName, args });
    if (this.offline) return false;

    if (this.socket) {
      return this.socket.invoke(methodName, ...args);
    }

    return new Promise(async resolve =>
      this.once("init", () => resolve(this.socket.invoke(methodName, ...args)))
    );
  }
}

if (!SignalR) {
  throw new Error("[Vue-SignalR] cannot locate signalr-client");
}

const SignalRPlugin = {
  install(Vue: typeof _Vue, options?: any | {}) {
    if (!options || !options.url) {
      throw new Error("[Vue-SignalR] cannot locate connection");
    }

    let url = options.url;
    let environment = "";
    let customerName = "";

    const authJson = localStorage.AuthorizationData;
    
    if (authJson != null) {
      let auth2: Oauthorization2Response = JSON.parse(authJson);
      environment = auth2.environment;

      if(auth2.data != null && auth2.data.information != null)
        customerName = auth2.data.information.customerName;
    }
    
    if(options.urlTest && customerName === "ImmoVietNam" && (environment === "dev" || environment === "staging"))
      url = options.urlTest;

    const Socket = new SocketConnection(url);
    Object.defineProperties(Vue.prototype, {
      $socket: {
        get() {
          return Socket;
        }
      }
    });
  }
};

export default SignalRPlugin;

declare module 'vue/types/vue' {
  interface Vue {
    $socket: ISocketConnection;
  }
}